import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Video, Plus, RotateCcw, Search, Users, CalendarClock } from "lucide-react";
import { MEETING_TYPES, MEETING_STATUSES, typeLabel, statusMeta, fmtDate } from "./meetingConstants";
import MeetingCreateDialog from "./MeetingCreateDialog";

export default function Meetings() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [mtype, setMtype] = useState("");
  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (mtype) params.meeting_type = mtype;
      if (q) params.q = q;
      const { data } = await api.get("/meetings", { params });
      setData(data);
    } catch (e) { toast.error(errMsg(e, "Could not load meetings.")); }
    finally { setLoading(false); }
  }, [status, mtype, q]);
  useEffect(() => { load(); }, [status, mtype]); // eslint-disable-line

  useEffect(() => {
    if (sp.get("new") === "1") { setCreateOpen(true); const n = new URLSearchParams(sp); n.delete("new"); setSp(n, { replace: true }); }
  }, []); // eslint-disable-line

  const prefill = {
    related_lead_id: sp.get("lead") || null,
    related_opportunity_id: sp.get("opportunity") || null,
    related_chat_session_id: sp.get("chat") || null,
  };
  const counts = data?.counts || {};
  const items = data?.items || [];

  return (
    <div className="pb-16" data-testid="meetings-page">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2"><Video size={22} className="text-[#1C4358]" /><h1 className="text-2xl font-display font-bold text-[#1C4358]">Meetings</h1></div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} className="border-[#D6E0E7] text-[#1C4358] gap-1"><RotateCcw size={14} /> Refresh</Button>
          <Button size="sm" data-testid="meeting-new-btn" onClick={() => setCreateOpen(true)} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white gap-1"><Plus size={15} /> New meeting</Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="meeting-status-tabs">
        <button onClick={() => setStatus("")} className={`text-[13px] font-semibold px-3 py-1.5 rounded-full border transition-colors ${status === "" ? "border-[#1C4358] bg-[#1C4358] text-white" : "border-[#D6E0E7] text-[#4B5563]"}`}>All</button>
        {MEETING_STATUSES.map((s) => (
          <button key={s.key} data-testid={`meeting-tab-${s.key}`} onClick={() => setStatus(s.key)}
            className="text-[13px] font-semibold px-3 py-1.5 rounded-full border transition-colors"
            style={{ borderColor: status === s.key ? s.hex : "#D6E0E7", color: status === s.key ? "#fff" : "#4B5563", backgroundColor: status === s.key ? s.hex : "white" }}>
            {s.label} <span className="tabular-nums opacity-80">{counts[s.key] ?? 0}</span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-2.5 text-[#9AA5B1]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search meeting titles…" className="h-9 pl-9 text-[14px]" data-testid="meeting-search" />
        </div>
        <select value={mtype} onChange={(e) => setMtype(e.target.value)} className="h-9 rounded-md border border-[#D6E0E7] text-[14px] px-2 text-[#1C4358]" data-testid="meeting-type-filter">
          <option value="">All types</option>
          {MEETING_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
      </div>

      {loading && !data ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#1C4358]" size={28} /></div> :
        items.length === 0 ? <div className="text-center py-16 text-[#4B5563]"><Video className="mx-auto mb-2 text-[#9AA5B1]" /> No meetings yet. Create one to get started.</div> : (
          <div className="space-y-2.5">
            {items.map((m) => {
              const sm = statusMeta(m.status);
              return (
                <Card key={m.id} data-testid={`meeting-row-${m.id}`} onClick={() => navigate(`/meetings/${m.id}`)}
                  className="p-4 border-[#E6EDF2] hover:border-[#1C4358]/40 hover:shadow-sm transition-all cursor-pointer">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-[#1C4358] text-[15px] truncate">{m.title}</div>
                      <div className="text-[12px] text-[#4B5563] mt-0.5 flex flex-wrap items-center gap-2">
                        <span>{typeLabel(m.meeting_type)}</span>
                        <span className="inline-flex items-center gap-1"><CalendarClock size={12} /> {fmtDate(m.scheduled_at)}</span>
                        <span className="inline-flex items-center gap-1"><Users size={12} /> Host: {m.host_name}</span>
                      </div>
                    </div>
                    <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full shrink-0" style={{ backgroundColor: `${sm.hex}18`, color: sm.hex }}>{sm.label}</span>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

      <MeetingCreateDialog open={createOpen} onOpenChange={setCreateOpen} prefill={prefill} />
    </div>
  );
}
